import { useState } from 'react';
import { Bars3Icon, XMarkIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Logo } from './Icons/Logo';
import { useUser } from '@/utils/useUser';
import Link from 'next/link';
import { Button } from './Button';
import { motion } from 'framer-motion';
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslations } from 'next-intl';

const DesktopDropdown = ({ label, children }) => (
    <div className="relative group">
      <button className="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900 font-medium">
        <span>{label}</span>
        <ChevronDownIcon className="w-4 h-4 transition-transform group-hover:rotate-180" />
      </button>

      <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-150 absolute left-0 mt-3 w-[340px] bg-white border border-gray-100 rounded-2xl shadow-xl p-4">
        {children}
      </div>
    </div>
);

const SectionHeader = ({ children }) => (
    <div className="text-xs uppercase tracking-wide text-gray-400 px-2 pt-2 pb-1">{children}</div>
);

const MenuLink = ({ href, children }) => (
    <Link href={href} className="block px-2 py-2 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-50">
      {children}
    </Link>
);

export const Navbar = () => {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [openMobile, setOpenMobile] = useState({ platform: false, company: false });
  const t = useTranslations('navbar');

  return (
      <nav className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3">
              <Logo className="h-10 w-auto text-gray-900" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex flex-1 items-center justify-center gap-8">
              {/* Platform */}
              <DesktopDropdown label={t('platform')}>
                <div className="grid gap-1">
                  <SectionHeader>{t('sections.forBusinesses')}</SectionHeader>
                  <MenuLink href="/affiliate-marketing">{t('links.affiliateMarketing')}</MenuLink>
                  <MenuLink href="/influencer-marketing">{t('links.influencerMarketing')}</MenuLink>
                  <MenuLink href="/referral-marketing">{t('links.referralMarketing')}</MenuLink>
                  <div className="h-3" />
                  <SectionHeader>{t('sections.forCreators')}</SectionHeader>
                  <MenuLink
                      href={process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL
                          ? `${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/signup`
                          : '/signup'}
                  >
                    {t('links.joinAsAffiliate')}
                  </MenuLink>
                </div>
              </DesktopDropdown>

              {/* Company */}
              <DesktopDropdown label={t('company')}>
                <div className="grid gap-1">
                  <MenuLink href="/about">{t('links.about')}</MenuLink>
                  <MenuLink href="/faq">{t('links.faq')}</MenuLink>
                  <MenuLink href="/blog">{t('links.blog')}</MenuLink>
                  <MenuLink href="/how-it-works">{t('links.howItWorks')}</MenuLink>
                </div>
              </DesktopDropdown>

              {/* Pricing */}
              <Link href="/pricing" className="text-gray-700 hover:text-gray-900 font-medium">
                {t('pricing')}
              </Link>
            </div>

            {/* Auth Buttons */}
            <div className="hidden lg:flex items-center gap-4 ml-auto">
              {user ? (
                  <Button
                      href="/dashboard"
                      className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-200"
                  >
                    {t('dashboard')}
                  </Button>
              ) : (
                  <>
                    <Button
                        href="/signin"
                        className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-200 font-[650]"
                    >
                      {t('signIn')}
                    </Button>
                    <Button
                        href="/signup"
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-200 font-[650]"
                    >
                      {t('getStarted')}
                    </Button>
                  </>
              )}
              <LanguageSwitcher className="shrink-0" />
            </div>

            {/* Mobile Menu Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden p-3 rounded-lg hover:bg-gray-50"
                aria-label="Toggle menu"
            >
              {isOpen ? <XMarkIcon className="w-7 h-7 text-gray-700" /> : <Bars3Icon className="w-7 h-7 text-gray-700" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
              <motion.div
                  initial={{ opacity: 0, y: -12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="lg:hidden absolute top-20 left-0 right-0 bg-white border-b shadow-lg"
              >
                <div className="px-6 py-5 space-y-4">
                  <div className="flex items-center justify-between pb-2">
                    <span className="text-sm font-semibold text-gray-700">{t('language')}</span>
                    <LanguageSwitcher variant="mobile" onPick={() => setIsOpen(false)} />
                  </div>

                  {/* Platform accordion */}
                  <button
                      onClick={() => setOpenMobile((s) => ({ ...s, platform: !s.platform }))}
                      className="w-full flex items-center justify-between py-2 text-left font-medium text-gray-800"
                  >
                    <span>{t('platform')}</span>
                    <ChevronRightIcon className={`w-5 h-5 transition-transform ${openMobile.platform ? 'rotate-90' : ''}`} />
                  </button>
                  {openMobile.platform && (
                      <div className="pl-3 space-y-1">
                        <SectionHeader>{t('sections.forBusinesses')}</SectionHeader>
                        <MenuLink href="/affiliate-marketing">{t('links.affiliateMarketing')}</MenuLink>
                        <MenuLink href="/influencer-marketing">{t('links.influencerMarketing')}</MenuLink>
                        <MenuLink href="/referral-marketing">{t('links.referralMarketing')}</MenuLink>
                        <SectionHeader>{t('sections.forCreators')}</SectionHeader>
                        <MenuLink
                            href={process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL
                                ? `${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/signup`
                                : '/signup'}
                        >
                          {t('links.joinAsAffiliate')}
                        </MenuLink>
                      </div>
                  )}

                  {/* Company accordion */}
                  <button
                      onClick={() => setOpenMobile((s) => ({ ...s, company: !s.company }))}
                      className="w-full flex items-center justify-between py-2 text-left font-medium text-gray-800"
                  >
                    <span>{t('company')}</span>
                    <ChevronRightIcon className={`w-5 h-5 transition-transform ${openMobile.company ? 'rotate-90' : ''}`} />
                  </button>
                  {openMobile.company && (
                      <div className="pl-3 space-y-1">
                        <MenuLink href="/about">{t('links.about')}</MenuLink>
                        <MenuLink href="/faq">{t('links.faq')}</MenuLink>
                        <MenuLink href="/blog">{t('links.blog')}</MenuLink>
                        <MenuLink href="/how-it-works">{t('links.howItWorks')}</MenuLink>
                      </div>
                  )}

                  {/* Pricing */}
                  <Link href="/pricing" className="block py-2 font-medium text-gray-800">
                    {t('pricing')}
                  </Link>

                  {/* Auth */}
                  <div className="pt-4 space-y-3 border-t border-gray-100">
                    {user ? (
                        <Button fullWidth href="/dashboard" className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 text-lg">
                          {t('dashboard')}
                        </Button>
                    ) : (
                        <>
                          <Button fullWidth href="/signin" className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 text-lg font-[650]">
                            {t('signIn')}
                          </Button>
                          <Button fullWidth href="/signup" className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 text-lg font-[650]">
                            {t('getStarted')}
                          </Button>
                        </>
                    )}
                  </div>
                </div>
              </motion.div>
          )}
        </div>
      </nav>
  );
};

export default Navbar;
